import {assist} from '@sanity/assist'
import {debugSecrets} from '@sanity/debug-preview-url-secret-plugin'
import {documentInternationalization} from '@sanity/document-internationalization'
import {googleMapsInput} from '@sanity/google-maps-input'
import {BookIcon} from '@sanity/icons/Book'
import {EnvelopeIcon} from '@sanity/icons/Envelope'
import {MobileDeviceIcon} from '@sanity/icons/MobileDevice'
import {PresentationIcon} from '@sanity/icons/Presentation'
import {SanityMonogram} from '@sanity/logos'
import {themerTool} from '@sanity/themer/tool'
import {visionTool} from '@sanity/vision'
import {
  defineConfig,
  defineField,
  definePlugin,
  type AuthProvider,
  type WorkspaceOptions,
} from 'sanity'
import {unsplashAssetSource, UnsplashIcon} from 'sanity-plugin-asset-source-unsplash'
import {internationalizedArray} from 'sanity-plugin-internationalized-array'
import {media} from 'sanity-plugin-media'
import {defineDocuments, defineLocations, presentationTool} from 'sanity/presentation'
import {structureTool} from 'sanity/structure'

import {imageAssetSource} from './assetSources/imageAssetSource'
import {
  Annotation,
  Block,
  CustomBadge,
  Field,
  formComponentsPlugin,
  InlineBlock,
  Input,
  Item,
  Preview,
} from './components/formComponents'
import {
  CustomLayout,
  CustomLogo,
  CustomNavbar,
  CustomToolMenu,
  studioComponentsPlugin,
} from './components/studioComponents'
import {resolveDocumentActions as documentActions} from './documentActions'
import {useTestVersionAction} from './documentActions/actions/TestVersionAction'
import {assistFieldActionGroup} from './fieldActions/assistFieldActionGroup'
import {resolveInitialValueTemplates} from './initialValueTemplates'
import {customInspector} from './inspectors/custom'
import {testStudioLocaleBundles} from './locales'
import {documentFormOnly} from './plugins/document-form-only/plugin'
import {errorReportingTestPlugin} from './plugins/error-reporting-test/plugin'
import {formBuilderReproTool} from './plugins/form-builder-repro/plugin'
import {autoCloseBrackets} from './plugins/input/auto-close-brackets-plugin'
import {wave} from './plugins/input/wave-plugin'
import {languageFilter} from './plugins/language-filter/plugin'
import {presenceDebug} from './plugins/presence-debug/plugin'
import {routerDebugTool} from './plugins/router-debug/plugin'
import {styleOutline} from './plugins/style-outline/plugin'
import {useArchiveAndDeleteCustomAction} from './releases/customReleaseActions'
import {createSchemaTypes} from './schema'
import {StegaDebugger} from './schema/debug/components/DebugStega'
import {CustomNavigator} from './schema/presentation/CustomNavigator'
import {types as presentationNextSanitySchemaTypes} from './schema/presentation/next-sanity'
import {types as presentationPreviewKitSchemaTypes} from './schema/presentation/preview-kit'
import {newDocumentOptions} from './structure/resolveNewDocumentOptions'
import {singletons} from './structure/resolveSingletons'
import {structure} from './structure/resolveStructure'
import {defaultDocumentNode} from './structure/resolveStructureDocumentNode'

// @ts-expect-error - defined by vite
const isStaging = globalThis.__SANITY_STAGING__ === true

// Set by `pnpm dev:proxy` / `pnpm dev:proxy:http1`: routes production-workspace requests through @repo/debug-proxy
// to exercise the studio under adverse network conditions (see packages/@repo/debug-proxy).
function getDebugProxyApiHost(): string | undefined {
  const mode = process.env.SANITY_STUDIO_USE_DEBUG_PROXY
  if (!mode) {
    return undefined
  }
  if (mode === 'true') {
    // HTTP/2 over TLS — requires the proxy cert to be trusted (see the package README)
    return 'https://localhost:3051'
  }
  if (mode === 'http1') {
    return 'http://localhost:3050'
  }
  console.warn(`Ignoring unknown SANITY_STUDIO_USE_DEBUG_PROXY value: "${mode}"`)
  return undefined
}

const debugProxyApiHost = getDebugProxyApiHost()

// Sanity Sandbox org SAML SSO (`ppsg7ml5`). `/auth/providers` reports `sso.saml: true`
// but does not include the login URL, so Studio will not offer SSO unless we add it.
// Same configuration ID as `dev/auth-test-studio`.
const sanitySandboxSsoProvider: AuthProvider = {
  name: 'saml',
  title: 'SSO',
  url: 'https://api.sanity.io/v2021-10-01/auth/saml/login/91cadf2a',
}

const sanitySandboxAuth = {
  providers: (prev: AuthProvider[]) =>
    prev.some((provider) => provider.name === sanitySandboxSsoProvider.name)
      ? prev
      : [...prev, sanitySandboxSsoProvider],
}

const envConfig = {
  // use this for production workspaces
  production: isStaging
    ? {apiHost: 'https://api.sanity.io'}
    : debugProxyApiHost
      ? {apiHost: debugProxyApiHost}
      : {},
  // use this for staging workspaces
  staging: isStaging ? {} : {apiHost: 'https://api.sanity.work'},
}

const sharedSettings = ({projectId}: {projectId: string}) => {
  return definePlugin({
    name: 'sharedSettings',
    schema: {
      types: createSchemaTypes(projectId),
      templates: resolveInitialValueTemplates,
    },
    form: {
      image: {
        assetSources: [imageAssetSource, unsplashAssetSource],
      },
      file: {
        assetSources: [imageAssetSource],
      },
    },

    i18n: {
      bundles: testStudioLocaleBundles,
    },

    mediaLibrary: {
      enabled: true,
    },

    advancedVersionControl: {
      enabled: true,
    },

    document: {
      actions: documentActions,
      inspectors: (prev, ctx) => {
        if (ctx.documentType === 'inspectorsTest') {
          return [customInspector, ...prev]
        }

        return prev
      },
      unstable_fieldActions: (prev, ctx) => {
        const defaultActions = [...prev]

        if (['fieldActionsTest', 'stringsTest'].includes(ctx.documentType)) {
          return [...defaultActions, assistFieldActionGroup]
        }

        return defaultActions
      },
      newDocumentOptions,
      singletons,
      comments: {
        enabled: true,
      },
      badges: (prev, context) => (context.schemaType === 'author' ? [CustomBadge, ...prev] : prev),
    },
    plugins: [
      structureTool({
        icon: BookIcon,
        structure,
        defaultDocumentNode,
      }),
      debugSecrets(),
      presentationTool({
        allowOrigins: ['https://*.sanity.dev', 'http://localhost:*'],
        previewUrl: {
          origin:
            process.env.SANITY_STUDIO_PREVIEW_IFRAME_ORIGIN ??
            (process.env.NODE_ENV === 'development'
              ? 'http://localhost:3334'
              : 'https://test-studio-preview-iframe.sanity.dev'),
          preview: '/',
        },
        resolve: {
          mainDocuments: defineDocuments([
            {
              route: '/',
              filter: `_type == "simpleBlock" && isMain`,
            },
          ]),
          locations: {
            simpleBlock: defineLocations({
              select: {title: 'title'},
              resolve: (doc) => {
                if (!doc?.title) return {}
                return {
                  locations: [
                    {
                      title: doc.title,
                      href: `/?${new URLSearchParams({title: doc.title})}`,
                    },
                  ],
                }
              },
            }),
            // Test document type for verifying DocumentLocation icon and showHref properties
            locationResolverTest: defineLocations({
              select: {title: 'title', slug: 'slug.current'},
              resolve: (doc) => {
                if (!doc?.title) return {message: 'Add a title to see locations', tone: 'caution'}
                return {
                  locations: [
                    {
                      title: 'Email Client View',
                      href: `/newsletter/${doc.slug ?? 'untitled'}/email`,
                      icon: EnvelopeIcon,
                      showHref: false,
                    },
                    {
                      title: 'Web View',
                      href: `/newsletter/${doc.slug ?? 'untitled'}`,
                      // Uses defaults: DesktopIcon, showHref: true
                    },
                    {
                      title: 'Mobile App',
                      href: `/newsletter/${doc.slug ?? 'untitled'}/app`,
                      icon: MobileDeviceIcon,
                      showHref: false,
                    },
                    {
                      title: 'In-store Display',
                      href: `/newsletter/${doc.slug ?? 'untitled'}/display`,
                      icon: PresentationIcon,
                      showHref: false,
                    },
                  ],
                  message: 'Preview this content in different contexts',
                }
              },
            }),
          },
        },
      }),
      languageFilter({
        defaultLanguages: ['nb'],
        supportedLanguages: [
          {id: 'ar', title: 'Arabic'},
          {id: 'en', title: 'English'},
          {id: 'nb', title: 'Norwegian (bokmål)'},
          {id: 'nn', title: 'Norwegian (nynorsk)'},
          {id: 'pt', title: 'Portuguese'},
          {id: 'es', title: 'Spanish'},
        ],
        types: ['languageFilterDebug'],
      }),
      googleMapsInput({
        apiKey: 'AIzaSyDDO2FFi5wXaQdk88S1pQUa70bRtWuMhkI',
        defaultZoom: 11,
        defaultLocation: {
          lat: 40.7058254,
          lng: -74.1180863,
        },
      }),
      visionTool({
        // uncomment to test
        //defaultApiVersion: '2025-02-05',
        beta: {redesign: {enabled: true}},
      }),
      themerTool(),
      routerDebugTool(),
      // Opt-in (Vercel test-studio). Must stay this exact member expression.
      ...(process.env.SANITY_STUDIO_STYLE_OUTLINE === 'true' ? [styleOutline()] : []),
      formBuilderReproTool(),
      errorReportingTestPlugin(),
      presenceDebug(),
      media(),
      wave(),
      autoCloseBrackets(),
      internationalizedArray({
        languages: [
          {id: 'en', title: 'English'},
          {id: 'fr', title: 'French'},
        ],
        defaultLanguages: ['en'],
        fieldTypes: ['string'],
      }),
      documentInternationalization({
        supportedLanguages: [
          {id: 'en', title: 'English'},
          {id: 'fr', title: 'French'},
          {id: 'es', title: 'Spanish'},
          {id: 'de', title: 'German'},
        ],
        schemaTypes: ['documentI18nTest'],
      }),
    ],
  })()
}

const defaultWorkspace = defineConfig({
  name: 'default',
  title: 'Test Studio',
  projectId: 'ppsg7ml5',
  dataset: 'test',
  ...envConfig.production,
  plugins: [sharedSettings({projectId: 'ppsg7ml5'})],
  auth: sanitySandboxAuth,

  onUncaughtError: (error, errorInfo) => {
    console.log(error)
    console.log(errorInfo)
  },
  basePath: '/test',
  icon: SanityMonogram,
  scheduledPublishing: {
    enabled: true,
    inputDateTimeFormat: 'MM/dd/yy h:mm a',
  },
  tasks: {
    enabled: true,
  },
  mediaLibrary: {
    enabled: true,
  },
  document: {
    actions: (prev, ctx) => {
      if (ctx.schemaType === 'restrictedVersionActionsTest') {
        return prev.filter(({action}) => action === 'publish')
      }
      if (ctx.schemaType === 'book' && ctx.releaseId) {
        return [useTestVersionAction, ...prev]
      }
      if (ctx.schemaType === 'author' && ctx.releaseId) {
        return [...prev, useTestVersionAction]
      }

      return prev
    },
  },
  releases: {
    actions: (prev, ctx) => {
      if (ctx.release.state === 'active') {
        return [...prev, useArchiveAndDeleteCustomAction]
      }
      return prev
    },
    metadata: {
      fields: [
        defineField({
          name: 'legalReview',
          title: 'Legal review',
          type: 'string',
          initialValue: 'notReviewed',
          options: {
            list: [
              {title: 'Not reviewed', value: 'notReviewed'},
              {title: 'Reviewed', value: 'reviewed'},
            ],
          },
        }),
        defineField({
          name: 'signOff',
          title: 'Sign-off',
          type: 'object',
          fields: [
            defineField({name: 'reviewer', title: 'Reviewer', type: 'string'}),
            defineField({name: 'approved', title: 'Approved', type: 'boolean'}),
          ],
        }),
      ],
    },
  },
  beta: {
    variants: {
      enabled: true,
      types: {
        variant: {
          conditions: async () => {
            // Mimics an api call to get the conditions
            await new Promise((resolve) => setTimeout(resolve, 1000))
            return [
              {
                name: 'audience',
                title: 'Audience',
                description: 'The group of visitors this content targets.',
                values: [
                  {
                    value: 'loyal',
                    title: 'Loyal customers',
                    description: 'Repeat purchasers and members.',
                  },
                  {
                    value: 'new',
                    title: 'New visitors',
                    description: 'First-time visitors to the site.',
                  },
                ],
              },
              {
                name: 'locale',
                title: 'Locale',
                description: 'The visitor language and region.',
                values: ['en-US', 'nb-NO', 'de-DE'],
              },
            ]
          },
        },
      },
    },
    reactActivityMode: {
      enabled: true,
    },
  },
})

export default defineConfig([
  {
    ...defaultWorkspace,
    name: 'default-hidden',
    title: 'Default Hidden',
    subtitle: 'Statically hidden and configured as the first (default) workspace',
    basePath: '/default-hidden',
    hidden: true,
  },
  {
    ...defaultWorkspace,
    releases: {
      ...defaultWorkspace.releases,
      metadata: {
        ...defaultWorkspace.releases?.metadata,
        fields: [
          ...(defaultWorkspace.releases?.metadata?.fields ?? []),
          defineField({
            name: 'reviewNote',
            title: 'Review note',
            type: 'string',
            validation: (rule) => rule.required().min(3),
          }),
        ],
      },
    },
  },
  {
    ...defaultWorkspace,
    title: 'Test Studio (variants disabled)',
    name: 'test-studio-variants-disabled',
    basePath: '/test-studio-variants-disabled',
    beta: {
      variants: {
        enabled: false,
      },
    },
  },
  {
    ...defaultWorkspace,
    name: 'comments-v2',
    title: 'Comments v2',
    subtitle: 'Default workspace with the comments v2 beta enabled',
    basePath: '/comments-v2',
    beta: {
      ...defaultWorkspace.beta,
      comments: {v2: true},
    },
  },
  {
    ...defaultWorkspace,
    projectId: 'nonexistent',
    name: 'nonexistent-project',
    title: 'Nonexistent project',
    subtitle: 'Workspace with a nonexistent project id',
    basePath: '/nonexistent-project',
  },
  {
    ...defaultWorkspace,
    dataset: 'nonexistent',
    name: 'nonexistent-dataset',
    title: 'Nonexistent dataset',
    subtitle: 'Workspace with a nonexistent dataset',
    basePath: '/nonexistent-dataset',
  },
  {
    ...defaultWorkspace,
    name: 'admin-only',
    title: 'Admin Only',
    subtitle: 'Hidden unless you have an administrator role',
    basePath: '/admin-only',
    hidden: ({currentUser}) => {
      if (currentUser === null) return false
      return !currentUser.roles.some((role) => role.name === 'administrator')
    },
  },
  {
    ...defaultWorkspace,
    name: 'always-hidden',
    title: 'Always Hidden',
    subtitle: 'You should never see this workspace because it is statically hidden',
    basePath: '/always-hidden',
    hidden: true,
  },
  {
    ...defaultWorkspace,
    name: 'us',
    title: 'Test Studio (US)',
    dataset: 'test-us',
    basePath: '/us',
  },
  {
    ...defaultWorkspace,
    name: 'no-releases',
    title: 'No releases',
    basePath: '/no-releases',
    document: {
      drafts: {enabled: true},
    },
    releases: {enabled: false},
  },
  {
    ...defaultWorkspace,
    name: 'no-releases-no-variants',
    title: 'No releases and no variants',
    basePath: '/no-releases-no-variants',
    document: {
      drafts: {enabled: true},
    },
    releases: {enabled: false},
    beta: {
      variants: {
        enabled: false,
      },
    },
  },
  {
    ...defaultWorkspace,
    name: 'secondary',
    title: 'Secondary test project',
    projectId: 'q5caobza',
    dataset: 'production',
    basePath: '/secondary',
    // Different project/org than Sanity Sandbox — keep the default providers.
    auth: undefined,
  },
  {
    ...defaultWorkspace,
    name: 'unsplash',
    title: 'Only Unsplash Asset Source',
    basePath: '/unsplash',
    icon: UnsplashIcon,
    // Testing the docs case that only allow Unsplash image uploads
    form: {
      image: {
        assetSources: () => [unsplashAssetSource],
        directUploads: false,
      },
    },
  },
  {
    name: 'partialIndexing',
    title: 'Partial Indexing',
    projectId: 'ppsg7ml5',
    dataset: 'partial-indexing-2',
    plugins: [sharedSettings({projectId: 'ppsg7ml5'})],
    basePath: '/partial-indexing',
    auth: sanitySandboxAuth,
    ...envConfig.production,
    search: {
      unstable_partialIndexing: {
        enabled: true,
      },
    },
    scheduledPublishing: {
      enabled: false,
    },
    unstable_tasks: {
      enabled: false,
    },
    mediaLibrary: {
      enabled: true,
    },
  },
  {
    name: 'playground',
    title: 'Test Studio (playground)',
    subtitle: 'Playground dataset',
    projectId: 'ppsg7ml5',
    dataset: 'playground',
    ...envConfig.production,
    plugins: [sharedSettings({projectId: 'ppsg7ml5'})],
    basePath: '/playground',
    auth: sanitySandboxAuth,
    beta: {
      eventsAPI: {
        releases: true,
      },
    },
    search: {
      strategy: 'groqLegacy',
    },
    mediaLibrary: {
      enabled: true,
    },
    advancedVersionControl: {
      enabled: true,
    },
  },
  {
    name: 'listener-events',
    title: 'Listener events debug',
    subtitle: 'Listener events debugging',
    projectId: 'ppsg7ml5',
    dataset: 'data-loss',
    ...envConfig.production,
    plugins: [sharedSettings({projectId: 'ppsg7ml5'})],
    basePath: '/listener-events',
    auth: sanitySandboxAuth,
    mediaLibrary: {
      enabled: true,
    },
  },
  {
    name: 'playground-partial-indexing',
    title: 'Test Studio (playground-partial-indexing)',
    subtitle: 'Playground dataset',
    projectId: 'ppsg7ml5',
    ...envConfig.production,
    dataset: 'playground-partial-indexing',
    plugins: [sharedSettings({projectId: 'ppsg7ml5'})],
    basePath: '/playground-partial-indexing',
    auth: sanitySandboxAuth,
    mediaLibrary: {
      enabled: true,
    },
  },
  {
    name: 'staging',
    title: 'Staging',
    subtitle: 'Staging dataset',
    projectId: 'exx11uqh',
    dataset: 'playground',
    ...envConfig.staging,
    plugins: [sharedSettings({projectId: 'exx11uqh'})],
    basePath: '/staging',
    auth: {
      loginMethod: 'token',
    },
    unstable_tasks: {
      enabled: true,
    },
    mediaLibrary: {
      enabled: true,
    },
    beta: {
      variants: {
        enabled: true,
      },
    },
  },
  {
    name: 'growth',
    title: 'Growth (staging)',

    projectId: 'qroupali',
    dataset: 'production',
    ...envConfig.staging,
    plugins: [sharedSettings({projectId: 'qroupali'})],
    basePath: '/growth',
    auth: {
      loginMethod: 'token',
    },
    unstable_tasks: {
      enabled: true,
    },
    mediaLibrary: {
      enabled: true,
    },
  },
  {
    name: 'media-library-playground',
    title: 'Media Library Playground (staging)',
    projectId: '5iedwjzw',
    dataset: 'production',
    ...envConfig.staging,
    plugins: [sharedSettings({projectId: '5iedwjzw'})],
    basePath: '/media-library-playground-staging',
    auth: {
      loginMethod: 'token',
    },
    mediaLibrary: {
      enabled: true,
    },
  },
  {
    name: 'media-library-playground-localdev',
    title: 'Media Library Playground (localdev-staging)',
    projectId: '5iedwjzw',
    dataset: 'production',
    ...envConfig.staging,
    plugins: [sharedSettings({projectId: '5iedwjzw'})],
    basePath: '/media-library-playground-localdev',
    auth: {
      loginMethod: 'token',
    },
    mediaLibrary: {
      enabled: true,
      __internal: {
        frontendHost: 'http://localhost:3002',
      },
    },
  },
  {
    name: 'playground-staging',
    title: 'playground (Staging)',
    projectId: 'exx11uqh',
    dataset: 'playground',
    ...envConfig.staging,
    plugins: [sharedSettings({projectId: 'exx11uqh'})],
    basePath: '/playground-staging',
    auth: {
      loginMethod: 'token',
    },
    mediaLibrary: {
      enabled: true,
    },
  },
  {
    name: 'custom-components',
    title: 'Test Studio',
    subtitle: 'Components API playground',
    projectId: 'ppsg7ml5',
    ...envConfig.production,
    dataset: 'test',
    plugins: [
      sharedSettings({projectId: 'ppsg7ml5'}),
      studioComponentsPlugin(),
      formComponentsPlugin(),
    ],
    basePath: '/custom-components',
    auth: sanitySandboxAuth,
    onUncaughtError: (error, errorInfo) => {
      console.log(error)
      console.log(errorInfo)
    },
    form: {
      components: {
        input: Input,
        field: Field,
        item: Item,
        preview: Preview,
        block: Block,
        inlineBlock: InlineBlock,
        annotation: Annotation,
      },
    },
    studio: {
      components: {
        layout: CustomLayout,
        logo: CustomLogo,
        navbar: CustomNavbar,
        toolMenu: CustomToolMenu,
      },
    },
    mediaLibrary: {
      enabled: true,
    },
  },
  {
    name: 'ai-assist',
    title: 'Sanity AI Assist',
    projectId: 'ppsg7ml5',
    dataset: 'test',
    ...envConfig.production,
    plugins: [sharedSettings({projectId: 'ppsg7ml5'}), assist()],
    basePath: '/ai-assist',
    auth: sanitySandboxAuth,
    mediaLibrary: {
      enabled: true,
    },
  },
  {
    name: 'stega',
    title: 'Debug Stega Studio',
    projectId: 'ppsg7ml5',
    dataset: 'test',
    ...envConfig.production,
    plugins: [sharedSettings({projectId: 'ppsg7ml5'})],
    basePath: '/stega',
    auth: sanitySandboxAuth,
    form: {
      components: {
        input: StegaDebugger,
      },
    },
    mediaLibrary: {
      enabled: true,
    },
  },
  {
    // Based on https://github.com/sanity-io/preview-kit/blob/195a476e5791421c5c8aa16275bad79a67b6ac58/apps/studio/sanity.config.ts#L42-L120
    name: 'presentation-preview-kit',
    title: 'Presentation with preview-kit',
    basePath: '/presentation-preview-kit',
    announcements: {enabled: false},
    scheduledPublishing: {enabled: false},
    tasks: {enabled: false},
    releases: {enabled: true},
    projectId: 'pv8y60vp',
    dataset: 'production',
    ...envConfig.production,
    schema: {types: presentationPreviewKitSchemaTypes},
    plugins: [
      structureTool(),
      presentationTool({
        allowOrigins: ({origin}) => ['https://preview-kit-*.sanity.dev', origin],
        previewUrl: {
          initial: 'https://preview-kit-next-app-router.sanity.dev',
          previewMode: ({origin, targetOrigin}) =>
            origin === targetOrigin
              ? false
              : {
                  enable: '/api/draft',
                },
        },
        resolve: {
          locations: {
            page: defineLocations({
              locations: [
                {title: 'App Router', href: 'https://preview-kit-next-app-router.sanity.dev/'},
                {title: 'Pages Router', href: 'https://preview-kit-next-pages-router.sanity.dev/'},
                {title: 'Remix', href: 'https://preview-kit-remix.sanity.dev/'},
              ],
            }),
          },
        },
        components: {
          unstable_navigator: {minWidth: 120, maxWidth: 240, component: CustomNavigator},
        },
      }),
      visionTool({beta: {redesign: {enabled: true}}}),
    ],
    mediaLibrary: {
      enabled: true,
    },
  },
  {
    // Based on https://github.com/sanity-io/next-sanity/blob/1d451c5aa606eb471e8dc4ddcd7ebf6253ae8eec/apps/mvp/sanity.config.ts#L5-L29
    name: 'presentation-next-sanity',
    title: 'Presentation with next-sanity',
    basePath: '/presentation-next-sanity',
    projectId: 'pv8y60vp',
    dataset: 'production',
    ...envConfig.production,
    schema: {types: presentationNextSanitySchemaTypes},
    plugins: [
      assist(),
      structureTool(),
      presentationTool({
        allowOrigins: ['https://*.sanity.dev'],
        previewUrl: {
          // Intentionally using sanity.build instead of sanity.dev, to test that it's able to recover from the server side domain redirect to sanity.dev
          // @TODO it is currently not able to recover, it fails eventually, investigate why
          initial: 'https://next.sanity.build',
          previewMode: {enable: '/api/draft-mode/enable'},
        },
      }),
      visionTool({beta: {redesign: {enabled: true}}}),
    ],
    mediaLibrary: {
      enabled: true,
    },
  },
  {
    name: 'document-form-only',
    title: 'Document form only',
    subtitle: 'A single document form: no navbar, no structure tool, no document form toolbar',
    projectId: 'ppsg7ml5',
    dataset: 'test',
    ...envConfig.production,
    auth: sanitySandboxAuth,
    basePath: '/document-form-only',
    plugins: [documentFormOnly()],
  },
]) as WorkspaceOptions[]
